import { useEffect, useRef, useState, type ReactNode, type Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import { Button, buttonClasses } from '@/design-system/Button'
import { celebrate } from '@/design-system/effects'
import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { Reveal } from '@/design-system/motion'
import { Card, Container, Overline, Skeleton } from '@/design-system/primitives'
import { useRubrics } from '@/features/event/useRubrics'
import { PublicLayout } from '@/layouts/PublicLayout'
import { ApiError, apiClient } from '@/lib/apiClient'
import { cn } from '@/lib/cn'
import { applyEventTheme } from '@/lib/theme'

import { useFeedbackForm, useSubmitFeedback } from './api'
import {
  RATING_KEYS,
  type FeedbackForm,
  type RatingKey,
  type RecommendationKey,
  type TextField,
} from './types'

/**
 * « Votre avis » — `/avis`, ouvert à tous par le QR de la salle ou le lien de
 * l'e-mail de remerciement (`?edition=<slug>`).
 *
 * **Anonyme.** Rien ici ne demande ni n'envoie qui répond : pas de compte, pas
 * de jeton personnel, pas de cookie. La seule chose gardée, c'est une marque
 * « déjà répondu » dans le stockage local de l'appareil — elle n'en sort
 * jamais et sert seulement à éviter un double envoi par mégarde (une autre
 * personne peut toujours répondre sur le même téléphone).
 *
 * Tous les textes (titre, questions, catégories, choix, remerciement,
 * recueil fermé) viennent du back-office : « Avis → Textes du formulaire ».
 */

type Ratings = Partial<Record<RatingKey, number>>
type Texts = Record<TextField, string>
type Missing = 'score' | 'ratings' | 'recommendation'

const SENT_KEY = 'leyamo.feedback.sent.'
const EMPTY_TEXTS: Texts = { liked: '', disliked: '', improve: '', suggestions: '' }

function readSent(slug: string): boolean {
  try {
    return Boolean(window.localStorage.getItem(SENT_KEY + slug))
  } catch {
    return false
  }
}

function writeSent(slug: string): void {
  try {
    window.localStorage.setItem(SENT_KEY + slug, new Date().toISOString().slice(0, 10))
  } catch {
    // Navigation privée : la marque douce manque, l'avis est bien parti.
  }
}

export function FeedbackPage() {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const edition = params.get('edition')
  const form = useFeedbackForm(edition)
  const { navItems } = useRubrics()
  const data = form.data

  useEffect(() => {
    if (data) applyEventTheme(data.event.theme)
  }, [data])

  useEffect(() => {
    document.title = data ? `${data.texts.title} — ${data.event.name}` : t('feedback.loading')
  }, [data, t])

  if (form.isPending) {
    return (
      <Container className="flex min-h-dvh flex-col justify-center gap-6 py-24">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-16 w-full max-w-xl" />
        <Skeleton className="h-48 w-full max-w-3xl" />
        <span className="sr-only">{t('state.loading')}</span>
      </Container>
    )
  }

  if (form.isError || !data) {
    const missing = form.error instanceof ApiError && form.error.status === 404
    return (
      <Container className="flex min-h-dvh flex-col justify-center py-24">
        <div className="mb-10 flex items-center justify-between gap-4">
          <img src="/leyamo.png" alt="LeYAMO" className="h-14 w-14 rounded-full" />
          <LanguageSwitcher />
        </div>
        <h1 className="font-display text-display-lg text-content-primary">
          {missing ? t('feedback.noEventTitle') : t('state.errorTitle')}
        </h1>
        <p className="mt-4 max-w-xl text-body-lg text-content-secondary">
          {missing ? t('feedback.noEventBody') : t('state.errorBody')}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          {!missing ? (
            <Button onClick={() => void form.refetch()}>{t('state.retry')}</Button>
          ) : null}
          <Link to="/" className={buttonClasses({ variant: 'outline' })}>
            {t('nav.home')}
          </Link>
        </div>
      </Container>
    )
  }

  return (
    <PublicLayout
      eventName={data.event.name}
      logoUrl={data.event.logo_url}
      availableLocales={data.event.locales}
      navItems={navItems}
    >
      <header className="relative isolate overflow-hidden pt-24 sm:pt-28">
        <div aria-hidden className="aurora -z-10 opacity-40" />
        <Container className="max-w-3xl">
          <Reveal>
            <Overline>{data.event.name}</Overline>
            <h1 className="mt-3 break-words font-display text-display-md text-content-primary">
              {data.texts.title}
            </h1>
            {data.texts.intro ? (
              <p className="mt-4 whitespace-pre-line text-body-lg text-content-secondary">
                {data.texts.intro}
              </p>
            ) : null}
          </Reveal>
        </Container>
      </header>

      <Container className="max-w-3xl pb-24 pt-10">
        {data.is_open ? (
          <OpenForm form={data} edition={edition} onClosed={() => void form.refetch()} />
        ) : (
          <Notice title={data.texts.closed_title ?? ''} body={data.texts.closed_body ?? ''} />
        )}
      </Container>
    </PublicLayout>
  )
}

function Notice({ title, body, children }: { title: string; body: string; children?: ReactNode }) {
  return (
    <Card className="text-center">
      <div
        aria-hidden
        className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-champagne-400/40 text-champagne-300"
      >
        <MaskIcon />
      </div>
      <h2 className="mt-5 break-words font-display text-heading-lg text-content-primary">{title}</h2>
      {body ? (
        <p className="mx-auto mt-3 max-w-xl whitespace-pre-line text-content-secondary">{body}</p>
      ) : null}
      {children}
    </Card>
  )
}

function OpenForm({
  form,
  edition,
  onClosed,
}: {
  form: FeedbackForm
  edition: string | null
  onClosed: () => void
}) {
  const { t, i18n } = useTranslation()
  const submit = useSubmitFeedback(edition)
  const slug = form.event.slug

  // Le premier jeton reçu date l'ouverture du formulaire. Changer de langue
  // relit les textes — et un jeton neuf — sans remettre le chronomètre à zéro.
  const token = useRef<string | null>(form.form_token)
  if (!token.current && form.form_token) token.current = form.form_token

  const [already, setAlready] = useState(() => readSent(slug))
  const [done, setDone] = useState(false)
  const [score, setScore] = useState<number | null>(null)
  const [ratings, setRatings] = useState<Ratings>({})
  const [recommendation, setRecommendation] = useState<RecommendationKey | null>(null)
  const [texts, setTexts] = useState<Texts>(EMPTY_TEXTS)
  const [website, setWebsite] = useState('')
  const [missing, setMissing] = useState<Missing[]>([])
  const [error, setError] = useState<string | null>(null)

  const sections = {
    score: useRef<HTMLFieldSetElement>(null),
    ratings: useRef<HTMLFieldSetElement>(null),
    recommendation: useRef<HTMLFieldSetElement>(null),
  }

  if (done) {
    return <Notice title={form.texts.thanks_title ?? ''} body={form.texts.thanks_body ?? ''} />
  }

  if (already) {
    return (
      <Notice title={form.texts.already_title ?? ''} body={form.texts.already_body ?? ''}>
        <Button variant="outline" className="mt-6" onClick={() => setAlready(false)}>
          {form.texts.another_button}
        </Button>
      </Notice>
    )
  }

  function validate(): Missing[] {
    const found: Missing[] = []
    if (score === null) found.push('score')
    if (RATING_KEYS.some((key) => !ratings[key])) found.push('ratings')
    if (!recommendation) found.push('recommendation')
    return found
  }

  function send(retried = false) {
    const found = validate()
    setMissing(found)
    setError(null)
    if (found.length > 0) {
      const first = sections[found[0]!].current
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      first?.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true })
      return
    }
    if (!token.current) return
    submit.mutate(
      {
        form_token: token.current,
        score: score!,
        ratings: ratings as Record<RatingKey, number>,
        recommendation: recommendation!,
        liked: texts.liked.trim() || null,
        disliked: texts.disliked.trim() || null,
        improve: texts.improve.trim() || null,
        suggestions: texts.suggestions.trim() || null,
        locale: i18n.language,
        website: website || null,
      },
      {
        onSuccess: () => {
          writeSent(slug)
          setDone(true)
          window.scrollTo({ top: 0, behavior: 'smooth' })
          celebrate()
        },
        onError: (failure) => {
          if (failure instanceof ApiError) {
            if (failure.code === 'FEEDBACK_CLOSED') return onClosed()
            if (failure.code === 'FEEDBACK_FORM_EXPIRED' && !retried) {
              // Formulaire resté ouvert trop longtemps : un jeton neuf, et
              // l'envoi repart — les réponses sont toujours là.
              token.current = null
              setError(t('feedback.error.retrying'))
              void apiClient
                .get<FeedbackForm>(
                  edition
                    ? `/public/feedback?edition=${encodeURIComponent(edition)}`
                    : '/public/feedback',
                )
                .catch(() => null)
                .then((fresh) => {
                  token.current = fresh?.form_token ?? null
                  if (token.current) {
                    window.setTimeout(() => send(true), (form.min_fill_seconds + 1) * 1000)
                  } else {
                    setError(t('feedback.error.generic'))
                  }
                })
              return
            }
            if (failure.status === 429) return setError(t('feedback.error.rateLimited'))
            if (failure.code === 'FEEDBACK_TOO_FAST') return setError(t('feedback.error.tooFast'))
            if (failure.status === 422) return setError(t('feedback.error.invalid'))
          }
          setError(t('feedback.error.generic'))
        },
      },
    )
  }

  const textField = (field: TextField, key: string, number: string) => (
    <FreeText
      key={field}
      number={number}
      label={form.texts[key] ?? ''}
      value={texts[field]}
      max={form.max_length}
      onChange={(value) => setTexts((current) => ({ ...current, [field]: value }))}
    />
  )

  return (
    <form
      noValidate
      className="space-y-6"
      onSubmit={(formEvent) => {
        formEvent.preventDefault()
        send()
      }}
    >
      {form.texts.anonymity ? (
        <p className="flex items-start gap-3 rounded-lg border border-line-subtle bg-bg-elevated/60 px-4 py-3 text-sm text-content-secondary">
          <span aria-hidden className="mt-0.5 shrink-0 text-champagne-300">
            <LockIcon />
          </span>
          <span className="min-w-0 whitespace-pre-line">{form.texts.anonymity}</span>
        </p>
      ) : null}

      <Step
        ref={sections.score}
        number="01"
        legend={form.texts.score_question ?? ''}
        error={missing.includes('score') ? t('feedback.required.score') : null}
      >
        <ScoreScale
          value={score}
          onChange={(value) => {
            setScore(value)
            setMissing((current) => current.filter((item) => item !== 'score'))
          }}
          low={form.texts.score_low ?? ''}
          high={form.texts.score_high ?? ''}
        />
      </Step>

      <Step
        ref={sections.ratings}
        number="02"
        legend={form.texts.categories_question ?? ''}
        error={missing.includes('ratings') ? t('feedback.required.ratings') : null}
      >
        <ul className="divide-y divide-line-subtle">
          {form.categories.map((category) => (
            <li
              key={category.key}
              className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
            >
              <span
                id={`rating-${category.key}`}
                className={cn(
                  'min-w-0 break-words text-content-primary',
                  missing.includes('ratings') && !ratings[category.key] && 'text-ember-300',
                )}
              >
                {category.label}
              </span>
              <StarRating
                labelledBy={`rating-${category.key}`}
                name={`rating-${category.key}`}
                value={ratings[category.key] ?? 0}
                onChange={(value) => {
                  const next = { ...ratings, [category.key]: value }
                  setRatings(next)
                  if (RATING_KEYS.every((key) => next[key])) {
                    setMissing((current) => current.filter((item) => item !== 'ratings'))
                  }
                }}
              />
            </li>
          ))}
        </ul>
      </Step>

      <div className="space-y-6">
        {textField('liked', 'liked_question', '03')}
        {textField('disliked', 'disliked_question', '04')}
        {textField('improve', 'improve_question', '05')}
      </div>

      <Step
        ref={sections.recommendation}
        number="06"
        legend={form.texts.recommend_question ?? ''}
        error={missing.includes('recommendation') ? t('feedback.required.recommendation') : null}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          {form.recommendations.map((choice) => {
            const checked = recommendation === choice.key
            return (
              <label
                key={choice.key}
                className={cn(
                  'flex min-h-14 cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 transition duration-[var(--dur-fast)]',
                  'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-champagne-400/70',
                  checked
                    ? 'border-champagne-400 bg-champagne-400/10 text-content-primary'
                    : 'border-line text-content-secondary hover:border-line-strong',
                )}
              >
                <input
                  type="radio"
                  name="recommendation"
                  value={choice.key}
                  checked={checked}
                  onChange={() => {
                    setRecommendation(choice.key)
                    setMissing((current) => current.filter((item) => item !== 'recommendation'))
                  }}
                  className="h-4 w-4 shrink-0 accent-[var(--ember-500)]"
                />
                <span className="min-w-0 break-words">{choice.label}</span>
              </label>
            )
          })}
        </div>
      </Step>

      {textField('suggestions', 'suggestions_question', '07')}

      {/* Le piège à robots : hors de l'écran, hors du parcours clavier, ignoré
          des lecteurs d'écran et des gestionnaires de mots de passe. */}
      <div aria-hidden className="absolute -left-[9999px] h-px w-px overflow-hidden">
        <label>
          Website
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(changeEvent) => setWebsite(changeEvent.target.value)}
          />
        </label>
      </div>

      {missing.length > 0 ? (
        <p role="alert" className="text-sm text-ember-300">
          {t('feedback.required.summary')}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-state-danger">
          {error}
        </p>
      ) : null}

      <Button
        type="submit"
        size="lg"
        className="h-auto min-h-14 w-full min-w-0 whitespace-normal py-3 sm:w-auto"
        disabled={submit.isPending}
      >
        {submit.isPending ? t('feedback.sending') : form.texts.submit}
      </Button>
    </form>
  )
}

const Step = function Step({
  ref,
  number,
  legend,
  error,
  children,
}: {
  ref: Ref<HTMLFieldSetElement>
  number: string
  legend: string
  error: string | null
  children: ReactNode
}) {
  return (
    <fieldset
      ref={ref}
      className={cn(
        'min-w-0 scroll-mt-24 rounded-lg border bg-bg-elevated p-5 sm:p-6',
        error ? 'border-ember-400/60' : 'border-line-subtle',
      )}
    >
      <legend className="sr-only">{legend}</legend>
      <div aria-hidden className="flex items-baseline gap-3">
        <span className="overline shrink-0 text-champagne-400">{number}</span>
        <span className="min-w-0 break-words font-display text-heading-md text-content-primary">
          {legend}
        </span>
      </div>
      <div className="mt-5">{children}</div>
      {error ? (
        <p role="alert" className="mt-4 text-sm text-ember-300">
          {error}
        </p>
      ) : null}
    </fieldset>
  )
}

function FreeText({
  number,
  label,
  value,
  max,
  onChange,
}: {
  number: string
  label: string
  value: string
  max: number
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  const id = `feedback-text-${number}`
  const near = value.length > max * 0.8
  return (
    <div className="rounded-lg border border-line-subtle bg-bg-elevated p-5 sm:p-6">
      <label htmlFor={id} className="flex items-baseline gap-3">
        <span aria-hidden className="overline shrink-0 text-champagne-400">
          {number}
        </span>
        <span className="min-w-0 break-words font-display text-heading-md text-content-primary">
          {label}
          <span className="ml-2 align-middle text-xs font-normal text-content-muted">
            {t('feedback.optional')}
          </span>
        </span>
      </label>
      <textarea
        id={id}
        rows={3}
        maxLength={max}
        value={value}
        onChange={(changeEvent) => onChange(changeEvent.target.value)}
        className="mt-4 w-full resize-y rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary transition placeholder:text-content-muted focus:border-champagne-400/60"
      />
      <p
        className={cn(
          'mt-1 text-right text-xs tabular-nums',
          near ? 'text-ember-300' : 'text-content-muted',
        )}
      >
        {t('feedback.counter', { count: value.length, max })}
      </p>
    </div>
  )
}

/** Onze cases, de 0 à 10 : une rangée sur ordinateur, deux sur téléphone. */
function ScoreScale({
  value,
  onChange,
  low,
  high,
}: {
  value: number | null
  onChange: (value: number) => void
  low: string
  high: string
}) {
  const { t } = useTranslation()
  return (
    <div>
      <div role="radiogroup" className="grid grid-cols-6 gap-2 sm:grid-cols-11">
        {Array.from({ length: 11 }, (_, score) => {
          const checked = value === score
          return (
            <label key={score} className="relative block">
              <input
                type="radio"
                name="score"
                value={score}
                checked={checked}
                onChange={() => onChange(score)}
                aria-label={t('feedback.scoreLabel', { score })}
                className="peer sr-only"
              />
              <span
                className={cn(
                  'flex h-12 cursor-pointer select-none items-center justify-center rounded-md border text-base tabular-nums transition duration-[var(--dur-fast)]',
                  'peer-focus-visible:ring-2 peer-focus-visible:ring-champagne-400/70',
                  checked
                    ? 'border-transparent bg-ember text-white shadow-ember'
                    : 'border-line text-content-secondary hover:border-champagne-400/60 hover:text-content-primary',
                )}
              >
                {score}
              </span>
            </label>
          )
        })}
      </div>
      <div className="mt-3 flex items-start justify-between gap-4 text-xs text-content-muted">
        <span className="min-w-0">0 — {low}</span>
        <span className="min-w-0 text-right">10 — {high}</span>
      </div>
      <p aria-live="polite" className="mt-3 min-h-6 text-sm text-champagne-300">
        {value !== null ? t('feedback.scoreChosen', { score: value }) : ''}
      </p>
    </div>
  )
}

/** Cinq étoiles, chacune une vraie case radio : clavier et lecteur d'écran compris. */
function StarRating({
  name,
  labelledBy,
  value,
  onChange,
}: {
  name: string
  labelledBy: string
  value: number
  onChange: (value: number) => void
}) {
  const { t } = useTranslation()
  const [hover, setHover] = useState(0)
  const shown = hover || value
  return (
    <div
      role="radiogroup"
      aria-labelledby={labelledBy}
      className="-mx-1 flex shrink-0 items-center"
      onMouseLeave={() => setHover(0)}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <label
          key={star}
          className="relative flex h-11 w-11 cursor-pointer items-center justify-center"
          onMouseEnter={() => setHover(star)}
        >
          <input
            type="radio"
            name={name}
            value={star}
            checked={value === star}
            onChange={() => onChange(star)}
            aria-label={t('feedback.starLabel', { count: star })}
            className="peer sr-only"
          />
          <span
            className={cn(
              'rounded-md p-1 transition duration-[var(--dur-fast)] peer-focus-visible:ring-2 peer-focus-visible:ring-champagne-400/70',
              star <= shown ? 'text-champagne-400' : 'text-content-muted/50',
            )}
          >
            <StarIcon filled={star <= shown} />
          </span>
        </label>
      ))}
    </div>
  )
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden>
      <path
        d="M12 2.8l2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.2l-5.7 3.1 1.2-6.4-4.7-4.4 6.4-.8z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="5" y="10.5" width="14" height="10" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" strokeLinecap="round" />
    </svg>
  )
}

/** Le loup de la nuit masquée : le seul ornement de la page. */
function MaskIcon() {
  return (
    <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.6">
      <path
        d="M3 11c4-2 8-2 13 1 5-3 9-3 13-1 0 6-3 10-7 10-3 0-4-2-6-2s-3 2-6 2c-4 0-7-4-7-10z"
        strokeLinejoin="round"
      />
      <ellipse cx="10.5" cy="14.5" rx="2.6" ry="1.8" />
      <ellipse cx="21.5" cy="14.5" rx="2.6" ry="1.8" />
    </svg>
  )
}
